import { RentRequest, RepaymentSchedule } from '../types'

/**
 * Calculate rent request financial breakdown
 * Principal + 25% Financier Interest + 25% Renta Fee
 */
export const calculateRentRequestFinancials = (
  principal: number,
  duration: number = 12,
) => {
  const financierInterest = principal * 0.25 // 25% p.a.
  const rentaFee = principal * 0.25 // 25% of principal
  const totalRepayment = principal + financierInterest + rentaFee
  const monthlyInstallment = totalRepayment / duration

  return {
    principal,
    financierInterest,
    rentaFee,
    totalRepayment,
    monthlyInstallment,
    duration,
  }
}

/**
 * Generate repayment schedule for a rent request
 */
export const generateRepaymentSchedule = (
  rentRequestId: string,
  principal: number,
  duration: number,
  startDate: Date,
): RepaymentSchedule[] => {
  const { financierInterest, rentaFee, monthlyInstallment } =
    calculateRentRequestFinancials(principal, duration)

  const principalPerMonth = principal / duration
  const interestPerMonth = financierInterest / duration
  const feePerMonth = rentaFee / duration

  const schedule: RepaymentSchedule[] = []

  for (let i = 0; i < duration; i++) {
    const dueDate = new Date(startDate)
    dueDate.setMonth(dueDate.getMonth() + i + 1)

    schedule.push({
      id: `${rentRequestId}-installment-${i + 1}`,
      rentRequestId,
      installmentNumber: i + 1,
      dueDate: dueDate.toISOString(),
      principalAmount: principalPerMonth,
      financierInterestAmount: interestPerMonth,
      rentaFeeAmount: feePerMonth,
      totalAmount: monthlyInstallment,
      status: 'pending',
      retryCount: 0,
      disbursedToFunders: false,
      disbursedToRenta: false,
    })
  }

  return schedule
}

/**
 * Calculate rent-to-income ratio
 * Should be ≤ 40% for eligibility
 */
export const calculateRentToIncomeRatio = (
  monthlyInstallment: number,
  monthlyIncome: number,
): number => {
  return (monthlyInstallment / monthlyIncome) * 100
}

/**
 * Check tenant eligibility for rent request
 */
export const checkTenantEligibility = (
  monthlyIncome: number,
  monthlyInstallment: number,
  employmentStatus: 'Permanent' | 'Contract' | 'Freelance',
  riskScore: number,
): { eligible: boolean; reasons: string[] } => {
  const reasons: string[] = []
  let eligible = true

  // Check rent-to-income ratio (≤ 40%)
  const ratio = calculateRentToIncomeRatio(monthlyInstallment, monthlyIncome)
  if (ratio > 40) {
    eligible = false
    reasons.push(`Rent exceeds 40% of income (${ratio.toFixed(1)}%)`)
  }

  // Check employment status (prefer salaried)
  if (employmentStatus === 'Freelance') {
    eligible = false
    reasons.push('Freelance employment not eligible at this time')
  }

  // Check risk score (Low or Medium only)
  if (riskScore > 60) {
    eligible = false
    reasons.push('Risk score too high for approval')
  }

  // Check minimum income
  if (monthlyIncome < 50000) {
    eligible = false
    reasons.push('Monthly income below minimum threshold (₦50,000)')
  }

  if (eligible) {
    reasons.push('All eligibility criteria met')
  }

  return { eligible, reasons }
}

/**
 * Calculate funder's expected yield from a commitment
 */
export const calculateFunderYield = (
  commitmentAmount: number,
  totalPrincipal: number,
  totalFinancierInterest: number,
): number => {
  const percentage = commitmentAmount / totalPrincipal
  return totalFinancierInterest * percentage
}

/**
 * Calculate platform revenue from a rent request
 */
export const calculatePlatformRevenue = (principal: number): number => {
  return principal * 0.25 // 25% Renta fee
}

/**
 * Calculate remaining balance for a rent request
 */
export const calculateRemainingBalance = (
  totalRepayment: number,
  paidInstallments: number,
  monthlyInstallment: number,
): number => {
  const paidAmount = paidInstallments * monthlyInstallment
  return Math.max(0, totalRepayment - paidAmount)
}

/**
 * Format currency (Nigerian Naira)
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount)
}

/**
 * Calculate portfolio diversification score
 */
export const calculateDiversificationScore = (
  investments: { amount: number; riskLevel: string }[],
): number => {
  if (investments.length === 0) return 0

  const riskDistribution = {
    Low: 0,
    Medium: 0,
    High: 0,
  }

  investments.forEach((inv) => {
    riskDistribution[inv.riskLevel as keyof typeof riskDistribution]++
  })

  // Perfect diversification = equal distribution across risk levels
  const total = investments.length
  const idealPerRisk = total / 3

  const variance = Object.values(riskDistribution).reduce((sum, count) => {
    return sum + Math.pow(count - idealPerRisk, 2)
  }, 0)

  // Convert to 0-100 score (lower variance = higher score)
  const maxVariance = Math.pow(total, 2)
  const score = Math.max(0, 100 - (variance / maxVariance) * 100)

  return Math.round(score)
}

/**
 * Calculate grace period end date
 */
export const calculateGracePeriodEnd = (
  dueDate: string,
  graceDays: number = 7,
): Date => {
  const date = new Date(dueDate)
  date.setDate(date.getDate() + graceDays)
  return date
}

/**
 * Check if payment is in grace period
 */
export const isInGracePeriod = (
  dueDate: string,
  graceDays: number = 7,
): boolean => {
  const now = new Date()
  const due = new Date(dueDate)
  const graceEnd = calculateGracePeriodEnd(dueDate, graceDays)

  return now > due && now <= graceEnd
}
